import type Ajv from "ajv";

import { AlternativeContext } from "./AlternativeContext";
import {
	arrayItemSchema,
	controlPath,
	enumOptions,
	generatedObjectUiSchema,
	generatedUiSchema,
	schemaAtScope,
	schemaType
} from "./schema";
import type {
	FormUiSchema,
	JsonSchemaDefinition
} from "./types";
import {
	dataAtPath,
	isRecord,
	isValueArray
} from "./values";
import { elementState } from "./visibility";

const omitted = Symbol("hidden form value");

/** A per-form visibility map, including controls whose renderers are not mounted. */
export class VisibilityContext {
	private readonly paths = new Map<string, boolean>();

	private readonly visibilityQueries = new Set<string>();

	public constructor(
		private readonly schema: JsonSchemaDefinition,
		private readonly data: unknown,
		private readonly ajv: Ajv,
		private readonly ui: FormUiSchema,
		private readonly alternatives = new AlternativeContext()
	) {
		this.visit(ui, schema, "", true);
	}

	public clearHiddenValues(): unknown {
		const value = this.clearValue(this.data, "", true);

		return value === omitted ? undefined : value;
	}

	public get hiddenPaths(): string[] {
		return [...this.paths.keys()].filter(path => !this.isVisible(path));
	}

	public isVisible(path: string): boolean {
		if(!this.paths.has(path) && !this.visibilityQueries.has(path)) {
			this.visibilityQueries.add(path);

			// Missing objects still have child rules. Follow only the requested branch
			// so recursive schemas stay bounded without creating any form values.
			this.visit(this.ui, this.schema, "", true, path);
		}

		let ancestor = path;

		while(!this.paths.has(ancestor) && ancestor !== "") {
			const separator = ancestor.lastIndexOf(".");

			ancestor = separator < 0 ? "" : ancestor.slice(0, separator);
		}

		if(this.paths.get(ancestor) !== false) {
			return true;
		}

		const prefix = path ? `${path}.` : "";

		return [...this.paths.entries()]
			.some(([child, visible]) => visible && child !== path && child.startsWith(prefix));
	}

	private visit(
		ui: FormUiSchema,
		context: JsonSchemaDefinition,
		basePath: string,
		parentVisible: boolean,
		queryPath?: string
	): void {
		const { visible } = elementState(ui, this.data, basePath, this.ajv, parentVisible);

		if(ui.type !== "Control") {
			for(const child of ui.elements ?? []) {
				this.visit(child, context, basePath, visible, queryPath);
			}

			return;
		}

		const scope = ui.scope ?? "#";
		const path = controlPath(scope, basePath);
		const schema = schemaAtScope(this.schema, scope, context);
		const value = dataAtPath(this.data, path);

		// A value remains exposed when any control for the same path is visible.
		this.paths.set(path, visible || this.paths.get(path) === true);

		if(enumOptions(schema).length > 0) {
			return;
		}

		const alternative = this.alternatives.get(schema, path, value, this.ajv, this.schema);

		if(alternative) {
			const selected = alternative.options[alternative.index];

			if(!selected) {
				return;
			}

			const detail: FormUiSchema = {
				type: "Control",
				scope: "#",
				options: ui.options
			};

			this.visit(detail, selected.schema, path, visible, queryPath);

			return;
		}

		const containsQuery = queryPath !== undefined
			&& queryPath !== path
			&& queryPath.startsWith(path ? `${path}.` : "");

		if(schemaType(schema) === "object" && (isRecord(value) || containsQuery)) {
			const detail = ui.options?.detail ?? generatedObjectUiSchema(schema, this.schema);

			this.visit(detail, schema, path, visible, queryPath);
		}

		if(schemaType(schema) === "array" && isValueArray(value)) {
			value.forEach((_, index) => {
				const itemSchema = arrayItemSchema(this.schema, schema, index);
				const detail = ui.options?.detail ?? generatedUiSchema(itemSchema, this.schema);
				const itemPath = path ? `${path}.${index}` : String(index);

				this.visit(detail, itemSchema, itemPath, visible, queryPath);
			});
		}
	}

	private clearValue(value: unknown, path: string, parentVisible: boolean): unknown {
		const visible = this.paths.get(path) ?? parentVisible;
		const prefix = path ? `${path}.` : "";
		const visibleDescendant = !visible && [...this.paths.entries()]
			.some(([child, shown]) => shown && child !== path && child.startsWith(prefix));

		if(!visible && !visibleDescendant) {
			return omitted;
		}

		if(isValueArray(value)) {
			return value.map((item, index) => {
				const child = this.clearValue(item, `${prefix}${index}`, visible);

				// Preserve row paths and tuple positions; arrays cannot have missing JSON keys.
				return child === omitted ? undefined : child;
			});
		}

		if(isRecord(value)) {
			const entries = Object.entries(value)
				.map(([key, item]) => [
					key,
					this.clearValue(item, `${prefix}${key}`, visible)
				] as const)
				.filter(([, item]) => item !== omitted);

			return Object.fromEntries(entries);
		}

		return visible ? value : omitted;
	}
}
